import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, TextField, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import PatientFamilyView from './patientFamilyView'
import PatientDocumentView from './patientDocumentView';
import CompletedNurseView from './completedNurseView';
import { HomeContext } from '../utils/contexts/homeContext';
import { ServiceFormContext } from '../utils/contexts/serviceFormContext';
import { FormMode, ServiceNurseStatus, ServiceStatus } from '../utils/extensions/constantEx'
import { GetDayJSValueFromUTCDate, GetNoToCurrencyFormat } from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import dayjs from 'dayjs';

export default function ServiceNurseViewForm(props) {
    const { selectedServiceType, expanded, handleAccordionChange } = props;
    const { ShowAPIError, OpenErrorAlert, homeState } = useContext(HomeContext);
    const { selectedNurse, selectedPatientService } = useContext(ServiceFormContext);

    const [serviceEndDate, setServiceEndDate] = useState('');
    const [patientFamilies, setPatientFamilies] = useState([]);
    const [patientDocuments, setPatientDocuments] = useState([]);
    const [isServiceClosed, setIsServiceClosed] = useState(false);
    const [isNurseServiceClosed, setIsNurseServiceClosed] = useState(false);
    const [serviceCompletedDutyDays, setServiceCompletedDutyDays] = useState(0);
    const [nurseCompletedDutyDays, setNurseCompletedDutyDays] = useState(0);

    useEffect(() => {
        if (selectedPatientService) {
            let tempServiceCompletedDutyDays = 0, tempNurseCompletedDays = 0;
            const serviceDutyDays = selectedPatientService.service_duty_days;
            const serviceStartDate = GetDayJSValueFromUTCDate(selectedPatientService.service_start_date);
            const nurseStartDate = GetDayJSValueFromUTCDate(selectedPatientService.nurse_start_date);
            const tempIsServiceClosed = selectedPatientService.service_status_id === ServiceStatus.Completed ? true : false;
            const tempIsNurseServiceClosed = selectedPatientService.nurse_status_id !== ServiceNurseStatus.Active ? true : false;

            let tempEndDate = serviceStartDate;
            let toDate = dayjs();
            let fromDate = dayjs();

            if (serviceStartDate && serviceDutyDays > 0) {
                tempEndDate = serviceStartDate.add(parseInt(serviceDutyDays) - 1, 'day')
                setServiceEndDate(tempEndDate.format(DATE_DISPLAY_FORMAT));
            } else {
                setServiceEndDate('');
            }

            if (!tempIsServiceClosed && serviceStartDate) {
                toDate = dayjs(toDate.year().toString() + '-' + (toDate.month() + 1).toString() + '-' + toDate.date().toString());
                fromDate = dayjs(serviceStartDate.year().toString() + '-' + (serviceStartDate.month() + 1).toString() + '-' +
                    serviceStartDate.date().toString());

                tempServiceCompletedDutyDays = toDate.diff(fromDate, 'day') + 1;
            } else if (serviceStartDate) {
                const tempServiceCompletedDate = GetDayJSValueFromUTCDate(selectedPatientService.service_completed_date);
                if (tempServiceCompletedDate) {
                    toDate = dayjs(tempServiceCompletedDate.year().toString() + '-' + (tempServiceCompletedDate.month() + 1).toString()
                        + '-' + tempServiceCompletedDate.date().toString());
                    fromDate = dayjs(serviceStartDate.year().toString() + '-' + (serviceStartDate.month() + 1).toString()
                        + '-' + serviceStartDate.date().toString());

                    tempServiceCompletedDutyDays = toDate.diff(fromDate, 'day') + 1;
                }
            }
            setIsServiceClosed(tempIsServiceClosed);
            setServiceCompletedDutyDays(tempServiceCompletedDutyDays);

            toDate = dayjs();
            if (!tempIsNurseServiceClosed && nurseStartDate) {
                toDate = dayjs(toDate.year().toString() + '-' + (toDate.month() + 1).toString() + '-' + toDate.date().toString());
                fromDate = dayjs(nurseStartDate.year().toString() + '-' + (nurseStartDate.month() + 1).toString() + '-' +
                    nurseStartDate.date().toString());

                tempNurseCompletedDays = toDate.diff(fromDate, 'day') + 1;
            } else if (nurseStartDate) {
                const tempNurseCompletedDate = GetDayJSValueFromUTCDate(selectedPatientService.nurse_completed_date);
                if (tempNurseCompletedDate) {
                    toDate = dayjs(tempNurseCompletedDate.year().toString() + '-' + (tempNurseCompletedDate.month() + 1).toString()
                        + '-' + tempNurseCompletedDate.date().toString());
                    fromDate = dayjs(nurseStartDate.year().toString() + '-' + (nurseStartDate.month() + 1).toString()
                        + '-' + nurseStartDate.date().toString());

                    tempNurseCompletedDays = toDate.diff(fromDate, 'day') + 1;
                }
            }
            setIsNurseServiceClosed(tempIsNurseServiceClosed);
            setNurseCompletedDutyDays(tempNurseCompletedDays);

            if (selectedPatientService.relatives && selectedPatientService.relatives.length > 0) {
                const tempPatientrRelatives = [];
                selectedPatientService.relatives.forEach((tempDbRelative) => {
                    const tempRelativeView = {
                        id: tempDbRelative.id,
                        name: tempDbRelative.family_name,
                        relation: tempDbRelative.family_relation,
                        phoneNo: tempDbRelative.family_phone_no
                    };
                    tempPatientrRelatives.push(tempRelativeView);
                });
                setPatientFamilies([...tempPatientrRelatives]);
            }

            const tempDocs = [];
            for (const tempDbDoc of selectedPatientService.documents) {
                const tempViewDoc = {
                    id: tempDbDoc.id,
                    documentId: tempDbDoc.document_id,
                    documentType: tempDbDoc.document_type,
                    no: tempDbDoc.document_no,
                    fileName: tempDbDoc.file_name,
                    fileType: tempDbDoc.file_type,
                    notes: tempDbDoc.notes,
                };
                tempDocs.push(tempViewDoc);
            }
            setPatientDocuments([...tempDocs]);
        }
    }, [selectedPatientService]);

    return (
        <Box sx={{ padding: "16px", maxHeight: '80vh', overflowY: 'auto' }}>
            <Stack>
                <Box sx={{ paddingBottom: "20px", paddingTop: "0" }}>
                    <Stack>
                        <Typography gutterBottom variant="subtitle2">
                            Service Details
                        </Typography>
                        <Divider />
                    </Stack>
                </Box>

                <Box>
                    <Grid container spacing={2}>
                        <Grid size={3}>
                            <TextField size="small" fullWidth
                                label="Service Type" value={selectedServiceType ? selectedServiceType.service_type_name : ''}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Start Date"
                                value={GetDayJSValueFromUTCDate(selectedPatientService.service_start_date).format(DATE_DISPLAY_FORMAT)}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={1}>
                            <TextField size="small" fullWidth type='number' label="Duty Days" value={selectedPatientService.service_duty_days}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            !isServiceClosed &&
                            <Grid size={2}>
                                <TextField size="small" fullWidth
                                    label="End Date" value={serviceEndDate}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Grid>
                        }
                        {
                            isServiceClosed &&
                            <Grid size={2}>
                                {
                                    <TextField size="small" fullWidth
                                        label="Completed Date"
                                        value={GetDayJSValueFromUTCDate(selectedPatientService.nurse_completed_date).format(DATE_DISPLAY_FORMAT)}
                                        slotProps={{
                                            input: {
                                                readOnly: true
                                            },
                                        }}
                                    />
                                }
                            </Grid>
                        }
                        <Grid size={2}>
                            <TextField size="small" fullWidth type='number'
                                label="Completed Duty Days"
                                value={serviceCompletedDutyDays}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            isServiceClosed &&
                            <Grid size={2}>
                                <TextField size="small" fullWidth type='number'
                                    label="Extended Duty Days"
                                    value={selectedPatientService.service_extended_duty_days}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Grid>
                        }
                        {
                            !isServiceClosed &&
                            <Grid size={2}>
                            </Grid>
                        }
                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Service Fee"
                                value={GetNoToCurrencyFormat(selectedPatientService.service_total_salary)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            isServiceClosed &&
                            <Grid size={2}>
                                <TextField size="small" fullWidth label="Extended Days Fee"
                                    value={GetNoToCurrencyFormat(selectedPatientService.service_extended_duty_days_fee)}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Grid>
                        }
                        <Grid size={2}>
                            <TextField size="small" fullWidth
                                label="Paid Amount" value={GetNoToCurrencyFormat(selectedPatientService.service_paid_amount)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth
                                label="Discount Amount" value={GetNoToCurrencyFormat(selectedPatientService.service_discount)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth
                                label="Refund Amount" value={GetNoToCurrencyFormat(selectedPatientService.service_refund_amount)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth
                                label="Balance Amount" value={GetNoToCurrencyFormat(selectedPatientService.service_balance_amount)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            !isServiceClosed &&
                            <Grid size={2}></Grid>
                        }
                    </Grid>
                </Box>

                <Box sx={{ paddingBottom: "20px", paddingTop: "20px" }}>
                    <Stack>
                        <Typography gutterBottom variant="subtitle2">
                            Nurse Details
                        </Typography>
                        <Divider />
                    </Stack>
                </Box>

                <Box>
                    <Grid container spacing={2}>
                        <Grid size={4}>
                            <TextField size="small" fullWidth
                                label="Nurse Name"
                                value={selectedNurse ? selectedNurse.nurseName : ''}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField size="small" fullWidth
                                label="Mobile No"
                                value={selectedNurse ? selectedNurse.phoneNo : ''}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth
                                label="Advance Amount" value={selectedNurse ? selectedNurse.advanceAmount : '0.00'}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>

                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Start Date"
                                value={GetDayJSValueFromUTCDate(selectedPatientService.nurse_start_date).format(DATE_DISPLAY_FORMAT)}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={1}>
                            <TextField size="small" fullWidth type='number' label="Duty Days" value={selectedPatientService.nurse_duty_days}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            !isNurseServiceClosed &&
                            <Grid size={2}>
                                <TextField size="small" fullWidth
                                    label="End Date" value={serviceEndDate}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Grid>
                        }
                        {
                            isNurseServiceClosed &&
                            <Grid size={2}>
                                {
                                    <TextField size="small" fullWidth
                                        label="Completed Date"
                                        value={GetDayJSValueFromUTCDate(selectedPatientService.nurse_completed_date).format(DATE_DISPLAY_FORMAT)}
                                        slotProps={{
                                            input: {
                                                readOnly: true
                                            },
                                        }}
                                    />
                                }
                            </Grid>
                        }
                        <Grid size={2}>
                            <TextField size="small" fullWidth type='number'
                                label="Completed Duty Days"
                                value={nurseCompletedDutyDays}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            isNurseServiceClosed &&
                            <Grid size={2}>
                                <TextField size="small" fullWidth type='number'
                                    label="Extended Duty Days"
                                    value={selectedPatientService.nurse_extended_duty_days}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Grid>
                        }
                        <Grid size={4}>
                        </Grid>

                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Nurse Salary"
                                value={GetNoToCurrencyFormat(selectedPatientService.nurse_salary)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        {
                            isNurseServiceClosed &&
                            <Grid size={3}>
                                <TextField size="small" fullWidth label="Nurse Ext. Days Salary"
                                    value={GetNoToCurrencyFormat(selectedPatientService.nurse_extended_duty_salary)}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Grid>
                        }
                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Paid Nurse Salary"
                                value={GetNoToCurrencyFormat(selectedPatientService.nurse_paid_salary)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Balance Nurse Salary"
                                value={GetNoToCurrencyFormat((selectedPatientService.nurse_salary + selectedPatientService.nurse_extended_duty_salary) - selectedPatientService.nurse_paid_salary)}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ "padding": "20px 0" }}>
                    <Stack>
                        <Typography gutterBottom variant="subtitle2">
                            Patient Details
                        </Typography>
                        <Divider />
                    </Stack>
                </Box>

                <Box>
                    <Grid container spacing={2}>
                        <Grid size={5}>
                            <TextField size="small" fullWidth
                                label="Patient Name" value={selectedPatientService.patient_name}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth label="Mobile No"
                                value={selectedPatientService.patient_phone1}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={3}>
                            <TextField size="small" fullWidth
                                label="Place" value={selectedPatientService.patient_place}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <TextField size="small" fullWidth type='number'
                                label="Delivery No" value={selectedPatientService.patient_delivery_no}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField size="small" fullWidth label="Address"
                                helperText="" multiline
                                rows={4} value={selectedPatientService.patient_address}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField size="small" fullWidth
                                label="Bus Route" helperText=""
                                multiline rows={4} value={selectedPatientService.patient_bus_route}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={4}>
                            <Stack spacing={2}>
                                <TextField size="small" fullWidth label="Phone 1"
                                    value={selectedPatientService.patient_phone2}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />

                                <TextField size="small" fullWidth label="Phone 2"
                                    value={selectedPatientService.patient_phone3}
                                    slotProps={{
                                        input: {
                                            readOnly: true
                                        },
                                    }}
                                />
                            </Stack>
                        </Grid>
                        <Grid size={8}>
                            <TextField size="small" fullWidth
                                label="Notes" helperText="" multiline
                                rows={4} value={selectedPatientService.service_remarks}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },
                                }}
                            />
                        </Grid>


                    </Grid>
                </Box>


                <Box>
                    <Grid container>
                        <Grid size={12}>
                            <Box sx={{ padding: "16px 0" }}>
                                <PatientFamilyView expanded={expanded} formOpenMode={FormMode.ViewMode} patientFamilies={patientFamilies}
                                    handleAccordionChange={handleAccordionChange}
                                />

                                <PatientDocumentView expanded={expanded} patientDocuments={patientDocuments}
                                    formOpenMode={FormMode.ViewMode} loggedUser={homeState.loggedUser}
                                    showAPIError={ShowAPIError} openErrorAlert={OpenErrorAlert}
                                    handleAccordionChange={handleAccordionChange} />

                                <CompletedNurseView expanded={expanded} completedNurses={selectedPatientService.completed_nurses} handleAccordionChange={handleAccordionChange} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Stack>
        </Box>

    );
}